import React, { useEffect, useMemo, useState } from "react";
import { useMindspaceStore } from "../../store/mindspaceStore";
import { useIsGuest } from "../../store/authStore";
import { trackEvent } from "../../services/analyticsService";
import { logDebug, logError } from "../../utils/logging";
// Reuse modal form styles for consistency
import styles from "../common/BusinessProfileModal.module.css";

type Tier1Key =
  | "product_service_description"
  | "target_audience_description"
  | "brand_voice";

type Tier2Key =
  | "unique_selling_proposition"
  | "key_benefits"
  | "business_name";

type FormKey = Tier1Key | Tier2Key;

type FormState = Record<FormKey, string>;

const TIER1_LABELS: Record<Tier1Key, string> = {
  product_service_description: "Produsul/Serviciul tău",
  target_audience_description: "Audiența țintă",
  brand_voice: "Vocea brandului",
};

const TIER2_LABELS: Record<Tier2Key, string> = {
  unique_selling_proposition: "Propoziția unică de vânzare (USP)",
  key_benefits: "Beneficii cheie",
  business_name: "Numele afacerii",
};

const initialForm: FormState = {
  product_service_description: "",
  target_audience_description: "",
  brand_voice: "",
  unique_selling_proposition: "",
  key_benefits: "",
  business_name: "",
};

const MindspaceInfoPage: React.FC = () => {
  const isGuest = useIsGuest();
  const {
    defaultMindspace,
    contextItems,
    getContextValue,
    updateContextItem,
    error: mindspaceError,
    clearError,
  } = useMindspaceStore();

  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<Partial<Record<FormKey, string>> & { general?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasExistingData = useMemo(
    () =>
      contextItems.some((i) =>
        [
          "product_service_description",
          "target_audience_description",
          "brand_voice",
          "unique_selling_proposition",
          "key_benefits",
          "business_name",
        ].includes(i.item_type)
      ),
    [contextItems]
  );

  // Initialize from store on mount/open
  useEffect(() => {
    clearError();
    setForm({
      product_service_description: getContextValue("product_service_description"),
      target_audience_description: getContextValue("target_audience_description"),
      brand_voice: getContextValue("brand_voice"),
      unique_selling_proposition: getContextValue("unique_selling_proposition"),
      key_benefits: getContextValue("key_benefits"),
      business_name: getContextValue("business_name"),
    });
    trackEvent("mindspace_info_viewed", {
      mindspace_id: defaultMindspace?.id,
      has_existing_data: hasExistingData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (key: FormKey, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<FormKey, string>> = {};
    const minLen = (v: string, n: number) => v.trim().length >= n;
    // Tier‑1 required
    if (!minLen(form.product_service_description, 10)) {
      next.product_service_description = "Descrierea trebuie să aibă cel puțin 10 caractere";
    }
    if (!minLen(form.target_audience_description, 10)) {
      next.target_audience_description = "Descrierea trebuie să aibă cel puțin 10 caractere";
    }
    if (!minLen(form.brand_voice, 5)) {
      next.brand_voice = "Vocea brandului trebuie să aibă cel puțin 5 caractere";
    }
    // Tier‑2 optional but if present enforce minimal length 3
    (Object.keys(TIER2_LABELS) as Tier2Key[]).forEach((k) => {
      const v = form[k].trim();
      if (v && v.length < 3) next[k] = "Introduceți cel puțin 3 caractere sau lăsați gol";
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleReset = () => {
    setForm({
      product_service_description: getContextValue("product_service_description"),
      target_audience_description: getContextValue("target_audience_description"),
      brand_voice: getContextValue("brand_voice"),
      unique_selling_proposition: getContextValue("unique_selling_proposition"),
      key_benefits: getContextValue("key_benefits"),
      business_name: getContextValue("business_name"),
    });
    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!defaultMindspace) return;
    if (isGuest) return; // read-only for guests
    if (!validate()) return;

    setIsSubmitting(true);
    clearError();
    try {
      const entries = Object.entries(form) as [FormKey, string][];
      const updates = entries
        .filter(([, v]) => v.trim() !== "")
        .map(([k, v]) => updateContextItem(k, v.trim()));
      await Promise.all(updates);
      trackEvent("mindspace_info_saved", {
        mindspace_id: defaultMindspace.id,
        updated_fields_count: updates.length,
      });
      logDebug("[MindspaceInfoPage] Saved context items", { count: updates.length });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      logError("[MindspaceInfoPage] Error saving context items", err);
      setErrors((prev) => ({ ...prev, general: msg }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ marginTop: 0 }}>Informații Mindspace</h1>
      {!defaultMindspace && (
        <p style={{ color: "#6b7280" }}>
          Se încarcă Mindspace‑ul sau nu este disponibil încă. Reîncarcă pagina dacă problema
          persistă.
        </p>
      )}

      {/* Info/CTA for guests */}
      {isGuest && (
        <div className={styles.emptyState} style={{ marginTop: 8 }}>
          <p>
            Ești în modul oaspete. Autentifică‑te pentru a salva profilul de afacere și a beneficia
            de răspunsuri mai relevante.
          </p>
        </div>
      )}

      {/* General error */}
      {(errors.general || mindspaceError) && (
        <div className={styles.generalError} style={{ marginBottom: 12 }}>
          {errors.general || mindspaceError}
        </div>
      )}

      <form className={styles.form} onSubmit={handleSubmit}>
        {/* Tier‑1 */}
        {(Object.keys(TIER1_LABELS) as Tier1Key[]).map((key) => (
          <div className={styles.fieldGroup} key={key}>
            <label className={styles.label} htmlFor={key}>
              {TIER1_LABELS[key]} *
            </label>
            {key === "brand_voice" ? (
              <input
                id={key}
                type="text"
                className={`${styles.input} ${errors[key] ? styles.error : ""}`}
                value={form[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                disabled={isSubmitting || isGuest}
                maxLength={100}
                placeholder="Ex: Prietenos și profesional, Autoritar și încrezător..."
              />
            ) : (
              <textarea
                id={key}
                className={`${styles.textarea} ${errors[key] ? styles.error : ""}`}
                rows={3}
                value={form[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                disabled={isSubmitting || isGuest}
                maxLength={500}
                placeholder={
                  key === "product_service_description"
                    ? "Descrie pe scurt produsul sau serviciul pe care îl oferi..."
                    : "Cine sunt clienții tăi ideali? (vârstă, interese, provocări...)"
                }
              />
            )}
            {errors[key] && <span className={styles.errorText}>{errors[key]}</span>}
          </div>
        ))}

        {/* Tier‑2 */}
        <div style={{ marginTop: 8 }}>
          {(Object.keys(TIER2_LABELS) as Tier2Key[]).map((key) => (
            <div className={styles.fieldGroup} key={key}>
              <label className={styles.label} htmlFor={key}>
                {TIER2_LABELS[key]} <span style={{ color: "#6b7280" }}>(opțional)</span>
              </label>
              <textarea
                id={key}
                className={`${styles.textarea} ${errors[key] ? styles.error : ""}`}
                rows={key === "business_name" ? 2 : 3}
                value={form[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                disabled={isSubmitting || isGuest}
                maxLength={key === "business_name" ? 120 : 500}
              />
              {errors[key] && <span className={styles.errorText}>{errors[key]}</span>}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <button
            type="button"
            onClick={handleReset}
            className={styles.cancelButton}
            disabled={isSubmitting}
          >
            Resetează
          </button>
          {!isGuest && (
            <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
              {isSubmitting ? "Se salvează..." : "Salvează"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default MindspaceInfoPage;
